import React from 'react';

import type { IntlShape } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import type { AiSuggestionsPlugin } from './aiSuggestionsPluginType';
import { acceptAllSuggestionsAction } from './editor-actions/accept-all-suggestions';
import { acceptSuggestionAction } from './editor-actions/accept-suggestion';
import { discardAllSuggestionsAction } from './editor-actions/discard-all-suggestions';
import { discardSuggestionAction } from './editor-actions/discard-suggestion';
import { hideSuggestionDiffAction } from './editor-actions/hide-suggestion-diff';
import { selectSuggestionAction } from './editor-actions/select-suggestion';
import { setUIVisibilityAction } from './editor-actions/set-ui-visibility';
import { showSuggestionDiffAction } from './editor-actions/show-suggestion-diff';
import { aiSuggestionsPluginKey, createPlugin } from './pm-plugins/main';
import { SuggestionCardContentComponent } from './ui/components/SuggestionCard/SuggestionCardContentComponent';
import { updateSuggestions } from './ui/utils/updateSuggestions';

export const aiSuggestionsPlugin: AiSuggestionsPlugin = ({ api, config }) => {
	const { contentAri, onShowSidebar } = config;

	const quickInsert = ({
		formatMessage,
	}: {
		formatMessage: IntlShape['formatMessage'];
	}): QuickInsertItem[] =>
		contentAri
			? [
					{
						title: formatMessage(aiSuggestionsMessages.fetchGenerateFromDocument),
						keywords: ['ai', 'suggest', 'rovo'],
						icon: () => <AiGenerativeTextIcon label="" />,
						action(_insert, state) {
							updateSuggestions({
								api,
								contentAri,
							});
							return state.tr;
						},
					},
				]
			: [
					{
						title: formatMessage(aiSuggestionsMessages.fetchGenerateFromAdf),
						keywords: ['ai', 'suggest', 'page', 'adf'],
						icon: () => <AiGenerativeTextIcon label="" />,
						action(_insert, state) {
							updateSuggestions({
								api,
								contentAri,
							});
							return state.tr;
						},
					},
				];

	return {
		name: 'aiSuggestions',
		actions: {
			acceptSuggestion: acceptSuggestionAction(api),
			discardSuggestion: discardSuggestionAction(api),
			// Deprecated - will remove in direct follow up - use SelectSuggestionAction instead
			showSuggestionDiff: showSuggestionDiffAction(api),
			// Deprecated - will remove in direct follow up - use SelectSuggestionAction instead
			hideSuggestionDiff: hideSuggestionDiffAction(api),
			acceptAllSuggestions: acceptAllSuggestionsAction(api),
			discardAllSuggestions: discardAllSuggestionsAction(api),
			selectSuggestion: selectSuggestionAction(api),
			setUIVisibility: setUIVisibilityAction(api),
		},
		getSharedState(editorState: EditorState | undefined) {
			if (!editorState) {
				return undefined;
			}
			const pluginState = aiSuggestionsPluginKey.getState(editorState);
			if (!pluginState) {
				return undefined;
			}
			return {
				isHidden: pluginState.isHidden,
				selectedSuggestion: pluginState.selectedSuggestion,
				suggestionById: pluginState.suggestionById,
				baseLocalIdBySuggestionId: pluginState.baseLocalIdBySuggestionId,
				suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
				orderedSuggestionIds: pluginState.orderedSuggestionIds,
			};
		},
		pmPlugins() {
			return [
				{
					name: 'aiSuggestionsPlugin',
					plugin: ({ getIntl, nodeViewPortalProviderAPI }) => {
						const intl = getIntl?.();
						return createPlugin({ api, intl, contentAri, nodeViewPortalProviderAPI });
					},
				},
			];
		},
		contentComponent({}) {
			return <SuggestionCardContentComponent api={api} onShowSidebar={onShowSidebar} />;
		},
		pluginsOptions: {
			quickInsert,
		},
	};
};
